import React from 'react';
import { FiClock, FiChevronDown, FiTag } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePermission from '@/hooks/usePermission';
import { ACTIVITY_BADGE_CLASSNAME } from '../activityStatusVariant';

// Mesmo padrão de linha do professor usado em Redação (EssayRow.jsx) — lista
// vertical em vez de grid de cards, com o mesmo shell (borda + shadow-card) e
// o bloco de ações à direita (CTA + menu ⋯).
//
// Curso, turma e "obrigatória" saíram do card: a API de Atividades
// Complementares só devolve course-id/classroom-id (sem nome) e não tem esse
// conceito pra atividade avulsa. O status também não vem mais agregado
// (envios x/x) — é só o texto cru de `activity-status-or-submitted-count`,
// exibido com a mesma cor (amber-soft) que a "Pendente de avaliação" já usava.
export function ComplementaryActivityRowTeacher({ activity, index = 0, onEdit, onDelete, onEvaluate }) {
  const classHoursLabel = activity.classHours < 2 ? `${activity.classHours} hora` : `${activity.classHours} horas`;
  const statusLabel = activity.statusLabel;
  const tags = activity.tags || [];

  const canEdit = usePermission('Editar atividades', 'Arquivos e atividades complementares');
  const canDelete = usePermission('Excluir atividades', 'Arquivos e atividades complementares');
  const canEvaluate = usePermission('Visualizar e avaliar respostas', 'Arquivos e atividades complementares');
  const showActionsMenu = canEdit || canDelete;

  const meta = [{ icon: FiClock, text: classHoursLabel }, ...(activity.scope === 'publico' ? [{ text: 'Público' }] : []), ...(tags.length > 0 ? [{ icon: FiTag, text: tags.join(' | ') }] : [])];

  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
      style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
      data-testid="complementary-activity-row-teacher-wrapper"
      data-title={activity.title}
    >
      <div
        className="min-w-[200px] flex-1"
        data-testid="complementary-activity-row-teacher"
        data-title={activity.title}
      >
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className="mb-0 line-clamp-2 text-base font-extrabold leading-snug"
            style={{ color: '#222731' }}
            title={activity.title}
          >
            {activity.title}
          </h3>

          {statusLabel && (
            <Badge
              variant="amber-soft"
              className={`shrink-0 ${ACTIVITY_BADGE_CLASSNAME}`}
            >
              {statusLabel}
            </Badge>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-semibold text-neutral-500">
          {meta.map((item, metaIndex) => (
            <span
              key={metaIndex}
              className="flex items-center gap-1.5"
            >
              {item.icon && <item.icon className="h-4 w-4 text-mission" />}
              {item.text}
            </span>
          ))}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {canEvaluate && (
          <PlayfulCardButton
            className="h-9 w-auto px-6"
            onClick={() => onEvaluate(activity)}
          >
            Avaliar
          </PlayfulCardButton>
        )}

        {showActionsMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <PlayfulCardButton
                variant="outline"
                aria-label="Opções"
                className="h-9 w-9 shrink-0 gap-0 p-0"
              >
                <FiChevronDown size={20} />
              </PlayfulCardButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              {canEdit && <DropdownMenuItem onSelect={() => onEdit(activity)}>Editar</DropdownMenuItem>}
              {canDelete && <DropdownMenuItem onSelect={() => onDelete(activity)}>Excluir</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
